import { useQueryClient } from "@tanstack/react-query"
import {
	CHAT_EVENT_NAMES,
	MARECZEK_EVENT_NAMES,
	type RealtimeMessage,
} from "@zplanem/responses"
import { useEffect } from "react"
import { getPusherClient } from "../realtime/pusher-client"
import { applyRealtimeToChatCaches } from "./use-chat"
import { applyMareczekEventToThinkingCache } from "./use-mareczek-thinking"

export function useSharedChatChannel(
	propertyId: string | undefined,
	userId: string | undefined,
): void {
	const queryClient = useQueryClient()
	useEffect(() => {
		if (!propertyId || !userId) return
		const channelName = `private-property-${propertyId}-chat`
		const channel = getPusherClient().subscribe(channelName)
		const handlers: Record<string, (data: unknown) => void> = {}
		for (const eventName of CHAT_EVENT_NAMES) {
			const handler = (data: unknown) =>
				applyRealtimeToChatCaches(
					queryClient,
					propertyId,
					userId,
					data as RealtimeMessage,
				)
			handlers[eventName] = handler
			channel.bind(eventName, handler)
		}
		// Mareczek "thinking" indicator for the shared chat (ZPL-152) — the API
		// pushes it on this resource-wide channel; route by payload threadId.
		for (const eventName of MARECZEK_EVENT_NAMES) {
			const handler = (data: unknown) => {
				applyMareczekEventToThinkingCache(queryClient, {
					event: eventName,
					payload: data,
				} as RealtimeMessage)
			}
			handlers[eventName] = handler
			channel.bind(eventName, handler)
		}
		return () => {
			for (const [eventName, handler] of Object.entries(handlers)) {
				channel.unbind(eventName, handler)
			}
			getPusherClient().unsubscribe(channelName)
		}
	}, [propertyId, userId, queryClient])
}
